import React, { Fragment, useRef, useState } from "react";
import {
  ColumnFiltersState,
  ExpandedState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useProducts } from "./hooks";
import Header from "./header";
import Footer from "./footer";
import { getAuthenticatedPages, getRoutes } from "@/utils/helpers";
import { Button } from "../../ui/button";
import { useNavigate } from "react-router-dom";
import SubTable from "./sub-table";
import { Product } from "@/types/product";
import storage from "@/utils/storage";
import useQueryVendor from "@/hooks/queries/useQueryVendor";

interface Props {
  data: Product[];
}

const Products: React.FC<Props> = ({ data: products }) => {
  const isProductsPage = getAuthenticatedPages().products;
  const navigate = useNavigate();
  const routes = getRoutes();
  const { columns } = useProducts();
  const statusFilter = storage.getItem("products_filter")?.status;
  const { data: vendor } = useQueryVendor();

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(
    statusFilter ? [{ id: "status", value: statusFilter }] : []
  );
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});
  const [expanded, setExpanded] = React.useState<ExpandedState>({});
  const refs = useRef<(HTMLTableCellElement | null)[]>([]);

  const table = useReactTable({
    data: products,
    columns,
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: (row) => row.original.variants?.length > 0,
    onExpandedChange: setExpanded,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      expanded,
    },
  });

  return (
    <div>
      <div className="w-full rounded-lg border border-gray-200 p-6">
        {!isProductsPage ? (
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-1.5 mb-8">
              <h3 className="text-2xl font-semibold">Products</h3>
              <p className="text-primary">
                Manage your products and view their sales performance.
              </p>
            </div>
            <div>
              <Button
                variant="link"
                className="text-primary"
                onClick={() => navigate(routes.products)}
              >
                View all
              </Button>
            </div>
          </div>
        ) : null}
        <Header table={table} />
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="h-12">
                {headerGroup.headers.map((header, index) => {
                  return (
                    <TableHead
                      key={header.id}
                      ref={(el) => {
                        refs.current[index] = el;
                      }}
                      style={{
                        width: header.getSize(),
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <Fragment key={row.id}>
                  <TableRow
                    className={`${row.getIsExpanded() ? "bg-slate-50 border-b-0" : "hover:bg-slate-50"} cursor-pointer transition-colors duration-300`}
                    data-state={row.getIsSelected() && "selected"}
                    onClick={row.getToggleExpandedHandler()}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  {row.getIsExpanded() && (
                    <TableRow className="bg-slate-50">
                      <TableCell colSpan={columns.length}>
                        <SubTable
                          variants={row.original.variants}
                          vendor={vendor}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <Footer table={table} />
    </div>
  );
};

export default Products;
